import imageCompression from 'browser-image-compression';

/** 超过该体积（字节）才考虑压缩 */
export const IMAGE_COMPRESS_MIN_BYTES = 1 * 1024 * 1024;
/** 长边超过该像素才考虑压缩 */
export const IMAGE_COMPRESS_MAX_LONG_EDGE = 1920;

export async function readImageLongEdge(file: File): Promise<number> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    try {
      return Math.max(bitmap.width, bitmap.height);
    } finally {
      bitmap.close?.();
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(Math.max(img.naturalWidth, img.naturalHeight));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };
    img.src = url;
  });
}

/** 长边 > 1920 或 文件 > 1MB 时需要压缩 */
export function shouldCompressImage(file: File, longEdge: number): boolean {
  return file.size > IMAGE_COMPRESS_MIN_BYTES || longEdge > IMAGE_COMPRESS_MAX_LONG_EDGE;
}

export type PrepareImageUploadOptions = {
  /** 上传体积上限（字节），压缩目标 maxSizeMB 据此换算 */
  maxUploadBytes: number;
};

/** 按 MIME 推断扩展名；压缩后常变成 jpeg */
function extForMime(mimeType: string): string {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/webp') return 'webp';
  if (mimeType === 'image/gif') return 'gif';
  return 'jpg';
}

/**
 * browser-image-compression 在部分环境返回无 name 的 Blob / name=blob 的 File，
 * FormData 会带上 filename="blob"，后端校验失败（400）。这里强制还原可读文件名。
 */
export function ensureNamedUploadFile(source: Blob | File, originalName: string): File {
  const type = source.type || 'image/jpeg';
  const raw = (originalName || '').trim().split(/[/\\]/).pop() || 'image';
  const stemRaw = raw.includes('.') ? raw.replace(/\.[^.]+$/, '') : raw;
  const stem = !stemRaw || stemRaw === 'blob' ? 'image' : stemRaw;
  // 压缩产物加 _tiny，便于区分原图与压缩图，如 礼券2_tiny.jpg
  const name = `${stem}_tiny.${extForMime(type)}`;

  if (source instanceof File && source.name === name && source.type === type) {
    return source;
  }
  return new File([source], name, { type, lastModified: Date.now() });
}

/**
 * 按业务规则决定是否压缩；不满足条件则原样返回。
 */
export async function prepareImageFileForUpload(
  file: File,
  options: PrepareImageUploadOptions,
): Promise<File> {
  let longEdge = 0;
  try {
    longEdge = await readImageLongEdge(file);
  } catch {
    return file;
  }
  if (!shouldCompressImage(file, longEdge)) {
    return file;
  }

  const maxSizeMB = options.maxUploadBytes / (1024 * 1024);
  if (!Number.isFinite(maxSizeMB) || maxSizeMB <= 0) {
    return file;
  }

  const compressed = await imageCompression(file, {
    maxSizeMB,
    maxWidthOrHeight: IMAGE_COMPRESS_MAX_LONG_EDGE,
    initialQuality: 0.8,
    useWebWorker: typeof Worker !== 'undefined',
    fileType: file.type || undefined,
  });

  return ensureNamedUploadFile(compressed, file.name);
}
